import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { getBrazeSDK, initBrazeCore } from '../../Config/Braze/braze.config'
import {
  useBrazeContentCards,
  BrazeContentCard,
  BrazePageTrackingComponent,
} from '../../Hooks'
import { useAuthContext } from '../AuthContext/AuthContext'
import {
  isDevEnvironment,
  subscribeToInAppMessages,
  logEvent,
  type BrazeMessage,
  normalizeError,
  AppError,
  defaultInAppMessageHandler,
} from '../../Utils'
import { ANALYTICS_TAGS } from '../../Constant'

type BrazeContextType = {
  brazeInitialized: boolean
  brazeError: AppError | null
  contentCards: BrazeContentCard[]
}

export const BrazeContext = createContext<BrazeContextType | null>(null)

export const useBraze = () => {
  const context = useContext(BrazeContext)
  if (!context) {
    throw new Error('useBraze must be used within a BrazeProvider')
  }
  return context
}

type BrazeProviderProps = {
  children: React.ReactNode
}

export const BrazeProvider: React.FC<BrazeProviderProps> = ({ children }) => {
  const [brazeInitialized, setBrazeInitialized] = useState<boolean>(false)
  const [brazeError, setBrazeError] = useState<AppError | null>(null)
  const { userId, authLoading } = useAuthContext()
  const [pendingMessage, setPendingMessage] = useState<BrazeMessage | null>(null)
  const subscriptionIdRef = useRef<string | undefined>(undefined)
  const lastUserIdRef = useRef<string | undefined>(undefined)

  const { contentCards } = useBrazeContentCards(brazeInitialized)

  const startBraze = useCallback(async () => {
    if (isDevEnvironment()) {
      console.log('[Braze] Starting Braze')
    }
    if (authLoading) return

    try {
      const braze = await getBrazeSDK()
      if (!braze) {
        throw new Error('Failed to load Braze SDK')
      }

      // Pass user ID if authenticated, undefined if anonymous
      const uid = userId ?? undefined

      if (isDevEnvironment()) {
        console.log('[Braze] Initializing with userId:', uid || 'anonymous')
      }

      const success = await initBrazeCore(uid, preSessionSetup)
      if (!success) {
        throw new Error('Failed to initialize Braze core')
      }

      // Track the user used for the current Braze session to avoid duplicate inits
      lastUserIdRef.current = uid

      setBrazeInitialized(true)
      setBrazeError(null)

      if (isDevEnvironment()) {
        console.log(
          '[Braze] Successfully initialized with user:',
          uid ? 'authenticated' : 'anonymous'
        )
      }
    } catch (error) {
      const normalizedError = normalizeError(error)
      console.error('[Braze] Initialization failed:', normalizedError)

      logEvent(ANALYTICS_TAGS.braze_provider_initialization_failed, {
        provider: 'braze',
        error: normalizedError.message,
        hasUserId: !!userId,
        product: 'web_main',
      })

      setBrazeError(normalizedError)
      setBrazeInitialized(false)
    }
  }, [authLoading])

  // Pre-session setup, subscribe, don't auto-show
  const preSessionSetup = (braze: any) => {
    // Cleanup any existing subscription
    if (subscriptionIdRef.current && typeof braze?.removeSubscription === 'function') {
      try {
        braze.removeSubscription(subscriptionIdRef.current)
        if (isDevEnvironment()) {
          console.log('[Braze] Cleaned up existing subscription')
        }
      } catch (cleanupError) {
        console.warn('[Braze] Error cleaning up existing subscription:', cleanupError)
      } finally {
        subscriptionIdRef.current = undefined
      }
    }

    subscriptionIdRef.current = subscribeToInAppMessages(
      braze,
      (message: BrazeMessage) => {
        if (isDevEnvironment()) {
          console.log('[Braze] Message received in pre-session setup:', message)
        }
        // IAMs have a closeMessage method
        if ('closeMessage' in message) {
          setPendingMessage(message)
        }
      }
    )

    if (isDevEnvironment()) {
      console.log(
        '[Braze] Pre-session setup complete, subscription ID:',
        subscriptionIdRef.current
      )
    }
  }

  useEffect(() => {
    // Wait for auth before initializing Braze
    if (!authLoading) {
      if (isDevEnvironment()) {
        console.log('[Braze] Auth completed, initializing Braze. User state:', {
          hasUserId: !!userId,
        })
      }
      startBraze()
    }
  }, [authLoading, userId, startBraze])

  // Handle user changes after initial initialization
  useEffect(() => {
    if (!brazeInitialized || authLoading) return
    if (userId === lastUserIdRef.current) return
    if (isDevEnvironment()) {
      console.log('[Braze] User changed, reinitializing Braze with new user:', {
        hasUserId: !!userId,
      })
    }
    startBraze()
  }, [userId, brazeInitialized, authLoading, startBraze])

  // After session start, show pending
  useEffect(() => {
    const showPending = async () => {
      if (!brazeInitialized || !pendingMessage) return
      const messageShown = await defaultInAppMessageHandler(pendingMessage)
      if (messageShown) {
        setPendingMessage(null)
      }
    }
    void showPending()
  }, [brazeInitialized, pendingMessage])

  // Remove subscription on unmount
  useEffect(() => {
    return () => {
      const cleanup = async () => {
        if (subscriptionIdRef.current) {
          try {
            const braze = await getBrazeSDK()
            if (braze && subscriptionIdRef?.current && typeof braze?.removeSubscription === 'function') {
              braze.removeSubscription(subscriptionIdRef.current)
              if (isDevEnvironment()) {
                console.log('[Braze] Successfully removed IAM subscription on unmount')
              }
            }
          } catch (e) {
            console.warn('[Braze] Failed to remove IAM subscription on unmount:', e)
            logEvent(ANALYTICS_TAGS.braze_subscription_cleanup_failed, {
              provider: 'braze',
              error: normalizeError(e)?.message,
              product: 'web_main',
            })
          } finally {
            subscriptionIdRef.current = undefined
          }
        }
      }
      void cleanup()
    }
  }, [])

  const contextValue = {
    brazeInitialized,
    brazeError,
    contentCards,
  }

  return (
    <BrazeContext.Provider value={contextValue}>
      {children}
      {brazeInitialized && (
        <>
          <BrazePageTrackingComponent brazeInitialized={brazeInitialized} />
        </>
      )}
    </BrazeContext.Provider>
  )
}
